import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { L, tr } from "@/lib/i18n";

/**
 * PresentationMode — Phase C Feature 10
 *
 * Full-screen guided sequence overlay. The actual scene/unit/etap content
 * underneath is rendered by PublicViewer reacting to `currentStep` (it
 * calls `applyStep` for each step). This component renders the chrome:
 *   - top progress bar
 *   - step title + description
 *   - bottom Previous / Next / Exit controls
 *
 * Transparent to background gestures so the buyer can still drag-rotate
 * the scene while the sequence chrome stays visible.
 */
export default function PresentationMode({
  steps, currentIndex, onPrev, onNext, onExit, lang, defaultLang, scenarioName = null, navigationDisabled = false,
}) {
  if (!steps?.length || currentIndex == null) return null;
  const step = steps[currentIndex];
  if (!step) return null;
  const title = tr(step, "title", lang, defaultLang) || step.title;
  const desc = tr(step, "description", lang, defaultLang) || step.description;
  const btn = tr(step, "button_label", lang, defaultLang) || step.button_label || L(lang, "next");
  const isLast = currentIndex === steps.length - 1;

  return (
    <>
      {/* Top progress strip */}
      <div className="absolute top-0 inset-x-0 z-40 pointer-events-none" data-testid="presentation-mode">
        <div className="h-1 bg-white/10">
          <div
            className="h-full bg-[#C4A47C] transition-all"
            style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
            data-testid="presentation-progress"
          />
        </div>
      </div>

      {/* Center bottom panel */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-40 max-w-xl w-[92%] pointer-events-auto" data-testid="presentation-panel">
        <div className="glass-panel rounded-2xl px-6 py-5 text-center" data-testid="presentation-toolbar">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55" data-testid="presentation-step-indicator">
            {scenarioName ? <><span data-testid="presentation-scenario-name">{scenarioName}</span> · </> : null}
            <span data-testid="presentation-step-counter">{L(lang, "step_of", currentIndex + 1, steps.length)}</span>
          </div>
          <div className="font-display text-xl md:text-2xl text-white tracking-tight mt-1.5" data-testid="presentation-step-title">
            {title}
          </div>
          {desc && (
            <div className="text-sm text-white/75 mt-2 leading-relaxed" data-testid="presentation-step-description">{desc}</div>
          )}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              onClick={onPrev}
              disabled={navigationDisabled || currentIndex === 0}
              className="pill-nav inline-flex items-center gap-1.5 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:pointer-events-none transition"
              data-testid="presentation-prev"
            >
              <ChevronLeft className="h-4 w-4" /> {L(lang, "previous")}
            </button>
            <button
              onClick={onExit}
              className="text-white/60 hover:text-white text-[10px] uppercase tracking-[0.3em] px-3 py-2"
              data-testid="presentation-exit"
            >
              <X className="h-3.5 w-3.5 inline" /> {L(lang, "exit")}
            </button>
            <button
              onClick={onNext}
              disabled={navigationDisabled || isLast}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#C4A47C] text-zinc-900 text-xs uppercase tracking-wider hover:bg-[#d1b48d] disabled:opacity-40 disabled:pointer-events-none transition"
              data-testid="presentation-next"
            >
              {btn} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
